import {
  ORGANIZATION_TYPE_LABELS,
  PRODUCT_HOLDING_STATUS_LABELS,
  PRODUCT_HOLDING_STATUSES,
  ORGANIZATION_TYPES,
  formatDate,
  formatNumber,
  type OrganizationSummaryDto,
} from '@nova/shared';
import { Building2, List, Map as MapIcon, Search, Target, Upload } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { EmptyState } from '../components/EmptyState.js';
import { GlassPanel } from '../components/GlassPanel.js';
import { SkeletonPanel } from '../components/Skeleton.js';
import { useAuth } from '../auth/AuthProvider.js';
import { cn } from '../lib/cn.js';
import { MarketImport } from '../features/sales/MarketImport.js';
import { NorwayMap } from '../features/sales/NorwayMap.js';
import { OrganizationDetail } from '../features/sales/OrganizationDetail.js';
import { ProductMultiSelect } from '../features/sales/ProductMultiSelect.js';
import {
  DataQualityBadges,
  ExpiryBadge,
  HoldingStatusBadge,
} from '../features/sales/marketWidgets.js';
import {
  useMarketFacets,
  useMarketMap,
  useMarketProducts,
  useOrganizations,
  type OrganizationFilters,
} from '../features/sales/useMarket.js';

type Tab = 'marked' | 'import';

export function MarkedPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isAdmin = useAuth().hasModule('SALES', 'ADMIN');
  const [tab, setTab] = useState<Tab>('marked');

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-ink flex items-center gap-2 text-xl">
            <Target size={20} aria-hidden /> Marked og kunder
          </h1>
          <p className="text-ink-soft mt-0.5 text-sm">
            White space-kartet — hvem i primærmarkedet har hva, og når kontraktene deres utløper.
          </p>
        </div>
        {isAdmin && (
          <div className="border-hairline flex gap-1 rounded-lg border p-0.5">
            <TabButton active={tab === 'marked'} onClick={() => setTab('marked')} icon={<Building2 size={14} />}>
              Marked
            </TabButton>
            <TabButton active={tab === 'import'} onClick={() => setTab('import')} icon={<Upload size={14} />}>
              Import
            </TabButton>
          </div>
        )}
      </header>

      {tab === 'marked' || !isAdmin ? <MarketList selectedId={id ?? null} /> : <MarketImport />}

      <OrganizationDetail id={id ?? null} onClose={() => navigate('/marked')} />
    </div>
  );
}

function TabButton({
  active,
  onClick,
  icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm transition-colors',
        active ? 'bg-accent/15 text-accent' : 'text-ink-soft hover:text-ink',
      )}
    >
      {icon}
      {children}
    </button>
  );
}

/** Which pane is visible on narrow screens (both show side by side on lg+). */
type MobileView = 'liste' | 'kart';

function MarketList({ selectedId }: { selectedId: string | null }) {
  const navigate = useNavigate();
  const [mobileView, setMobileView] = useState<MobileView>('liste');
  const [filters, setFilters] = useState<OrganizationFilters>({ sort: 'name' });
  const { data, isLoading } = useOrganizations(filters);
  const { data: facets } = useMarketFacets();
  const { data: products = [] } = useMarketProducts();

  const expiringActive = filters.expiringWithinDays !== undefined;
  const productIds = filters.productIds ?? [];
  const open = (id: string) => navigate(`/marked/${id}`);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search size={14} className="text-ink-soft absolute left-2.5 top-1/2 -translate-y-1/2" aria-hidden />
          <input
            className="border-hairline bg-base/40 text-ink w-56 rounded-lg border py-1.5 pl-8 pr-3 text-sm"
            placeholder="Søk kommune / kommunenr / org.nr"
            value={filters.search ?? ''}
            onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value || undefined }))}
          />
        </div>
        <ProductMultiSelect
          products={products}
          selected={productIds}
          onChange={(ids) => setFilters((f) => ({ ...f, productIds: ids.length ? ids : undefined }))}
        />
        <Select
          value={filters.marketId ?? ''}
          onChange={(v) => setFilters((f) => ({ ...f, marketId: v || undefined }))}
          options={[['', 'Alle markeder'], ...(facets?.markets ?? []).map((m) => [m.id, m.name] as [string, string])]}
        />
        <Select
          value={filters.fylke ?? ''}
          onChange={(v) => setFilters((f) => ({ ...f, fylke: v || undefined }))}
          options={[['', 'Alle fylker'], ...(facets?.fylker ?? []).map((f) => [f, f] as [string, string])]}
        />
        <Select
          value={filters.competitor ?? ''}
          onChange={(v) => setFilters((f) => ({ ...f, competitor: v || undefined }))}
          options={[['', 'Alle CMS/konkurrenter'], ...(facets?.competitors ?? []).map((c) => [c, c] as [string, string])]}
        />
        <Select
          value={filters.holdingStatus ?? ''}
          onChange={(v) => setFilters((f) => ({ ...f, holdingStatus: v || undefined }))}
          options={[['', 'Alle statuser'], ...PRODUCT_HOLDING_STATUSES.map((s) => [s, PRODUCT_HOLDING_STATUS_LABELS[s]] as [string, string])]}
        />
        <Select
          value={filters.type ?? ''}
          onChange={(v) => setFilters((f) => ({ ...f, type: v || undefined }))}
          options={[['', 'Alle typer'], ...ORGANIZATION_TYPES.map((t) => [t, ORGANIZATION_TYPE_LABELS[t]] as [string, string])]}
        />
        <Select
          value={filters.sort ?? 'name'}
          onChange={(v) => setFilters((f) => ({ ...f, sort: v as OrganizationFilters['sort'] }))}
          options={[
            ['name', 'Sorter: navn'],
            ['expiry', 'Sorter: utløper snart'],
            ['population', 'Sorter: størrelse'],
          ]}
        />
        <Toggle
          active={expiringActive}
          onClick={() =>
            setFilters((f) => ({
              ...f,
              expiringWithinDays: expiringActive ? undefined : 548,
              sort: expiringActive ? f.sort : 'expiry',
            }))
          }
        >
          Utløper innen 18 mnd
        </Toggle>
        {/* Narrow screens flip between list and map; wide screens show both. */}
        <div className="border-hairline ml-auto flex gap-1 rounded-lg border p-0.5 lg:hidden">
          <ViewButton active={mobileView === 'liste'} onClick={() => setMobileView('liste')} icon={<List size={14} />}>
            Liste
          </ViewButton>
          <ViewButton active={mobileView === 'kart'} onClick={() => setMobileView('kart')} icon={<MapIcon size={14} />}>
            Kart
          </ViewButton>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(340px,420px)] lg:items-start">
        {/* List pane */}
        <div className={cn('space-y-2', mobileView === 'kart' && 'hidden lg:block')}>
          {isLoading ? (
            <SkeletonPanel lines={6} />
          ) : !data || data.items.length === 0 ? (
            <GlassPanel>
              <EmptyState
                title="Ingen organisasjoner å vise."
                description="Importer CMS-kartleggingen for å fylle white space-kartet, eller opprett en organisasjon."
              />
            </GlassPanel>
          ) : (
            <>
              <p className="text-ink-soft text-xs">{data.total} organisasjoner</p>
              {data.items.map((o) => (
                <OrgRow key={o.id} org={o} active={o.id === selectedId} onOpen={() => open(o.id)} />
              ))}
            </>
          )}
        </div>

        {/* Map pane — sticky so it stays visible while the list scrolls. */}
        <div
          className={cn(
            'lg:sticky lg:top-4',
            mobileView === 'liste' && 'hidden lg:block',
          )}
        >
          <MarketMapView
            productIds={productIds}
            statuses={filters.holdingStatus ? [filters.holdingStatus] : []}
            marketId={filters.marketId}
            selectedId={selectedId}
            onOpen={open}
          />
        </div>
      </div>
    </div>
  );
}

function MarketMapView({
  productIds,
  statuses,
  marketId,
  selectedId,
  onOpen,
}: {
  productIds: string[];
  statuses: string[];
  marketId?: string;
  selectedId: string | null;
  onOpen: (organizationId: string) => void;
}) {
  const { data, isLoading } = useMarketMap({ productIds, statuses, marketId });

  return (
    <GlassPanel>
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="font-display text-ink text-sm">Norgeskart</h2>
        <p className="text-ink-soft text-xs">
          {isLoading
            ? 'Laster …'
            : `${data?.withHoldings ?? 0} av ${data?.total ?? 0} kommuner med treff`}
        </p>
      </div>
      <NorwayMap cells={data?.cells ?? []} selectedId={selectedId} onSelect={(c) => onOpen(c.organizationId)} />
    </GlassPanel>
  );
}

function ViewButton({
  active,
  onClick,
  icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs transition-colors',
        active ? 'bg-accent/15 text-accent' : 'text-ink-soft hover:text-ink',
      )}
    >
      {icon}
      {children}
    </button>
  );
}

function OrgRow({
  org,
  active,
  onOpen,
}: {
  org: OrganizationSummaryDto;
  active?: boolean;
  onOpen: () => void;
}) {
  // The primary holding drives the row summary (the CMS-kartlegging seeds one).
  const holding = org.holdings[0];
  return (
    <button
      onClick={onOpen}
      aria-current={active ? 'true' : undefined}
      className={cn(
        'block w-full rounded-lg border p-3 text-left transition-colors',
        active
          ? 'border-accent/50 bg-accent/10'
          : 'border-hairline bg-panel/60 hover:bg-panel/90',
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-ink truncate text-sm font-medium">{org.name}</span>
            {holding && <HoldingStatusBadge status={holding.status} />}
            {org.isSeed && <span className="text-ink-soft text-[10px]">demo</span>}
          </div>
          <p className="text-ink-soft mt-0.5 text-xs">
            {ORGANIZATION_TYPE_LABELS[org.type]}
            {org.fylke ? ` · ${org.fylke}` : ''}
            {org.population ? ` · ${formatNumber(org.population)} innb.` : ''}
            {holding?.competitor ? ` · ${holding.competitor}` : ''}
          </p>
          {holding && (
            <div className="mt-1.5">
              <DataQualityBadges confidence={holding.confidence} liveVerified={holding.liveVerified} />
            </div>
          )}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          {holding && (
            <ExpiryBadge
              contractEnd={holding.contractEnd}
              daysToExpiry={holding.daysToExpiry}
              formatted={holding.contractEnd ? formatDate(holding.contractEnd) : ''}
            />
          )}
          {org.holdings.length > 1 && (
            <span className="text-ink-soft/60 text-[11px]">{org.holdings.length} produkter</span>
          )}
        </div>
      </div>
    </button>
  );
}

function Select({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: [string, string][];
}) {
  return (
    <select
      className="border-hairline bg-base/40 text-ink rounded-lg border px-3 py-1.5 text-sm"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {options.map(([v, label]) => (
        <option key={v} value={v}>
          {label}
        </option>
      ))}
    </select>
  );
}

function Toggle({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'rounded-lg border px-3 py-1.5 text-xs transition-colors',
        active ? 'bg-accent/15 text-accent border-accent/30' : 'border-hairline text-ink-soft hover:text-ink',
      )}
    >
      {children}
    </button>
  );
}
